import { FC, useEffect, useRef } from 'react';
import { Bot } from 'lucide-react';
import { marked } from 'marked';
import { useTranslate } from 'hooks/useTranslate';

import AiAgentAvatar from './AiAgentAvatar';
import AiAgentTypewriter from './AiAgentTypewriter';

marked.setOptions({
  breaks: true,
  gfm: true,
});

export interface AiAgentConversationMessage {
  id: string;
  type: 'question' | 'response';
  content: string;
  timestamp: Date;
}

type AiAgentConversationProps = {
  hasStartedChat: boolean;
  conversations: AiAgentConversationMessage[];
  isProcessing: boolean;
  userAvatarUrl: string;
};

const AiAgentConversation: FC<AiAgentConversationProps> = ({
  hasStartedChat,
  conversations,
  isProcessing = false,
  userAvatarUrl,
}) => {
  const { t } = useTranslate();

  const conversationEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (conversationEndRef.current) {
      conversationEndRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    }
  }, [conversations, isProcessing]);

  return (
    hasStartedChat && (
      <div className="conversation-history">
        {conversations.map((message) => (
          <div key={message.id} className={`message ${message.type}`}>
            <div className="message-wrapper">
              <AiAgentAvatar
                src={message.type === 'question' ? userAvatarUrl : undefined}
                alt={message.type === 'question' ? 'User' : 'AI Agent'}
                fallback={message.type === 'question' ? 'U' : <Bot />}
              />
              <div className="message-content">
                {message.type === 'question' ? (
                  <div className="question-content">
                    <div className="message-label">{t('AI_YouAsked')}:</div>
                    <div className="message-text">{message.content}</div>
                  </div>
                ) : (
                  <div className="response-content">
                    <div className="message-label">Sttoky:</div>
                    <div className="message-text">
                      <AiAgentTypewriter text={message.content} speed={20} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}

        {isProcessing && (
          <div className="message response">
            <div className="message-content">
              <div className="response-content">
                <div className="message-label">AI Agent:</div>
                <div className="message-text">
                  <div className="typing-indicator">
                    <span />
                    <span />
                    <span />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div ref={conversationEndRef} className="conversation-end" />
      </div>
    )
  );
};

export default AiAgentConversation;
